import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { ShowGenresField } from "@/pages/movies/components/fields/show-genres-field";
import { PlayCastField } from "@/pages/plays/components/fields/play-cast-field";
import {
  PlayEditFormType,
  type PlayOptionalFieldPresence,
  buildPlayEditSchema,
  normalizePlayRecord,
  playFieldPresence,
} from "@/pages/plays/schemas/play.edit.form";
import { useTheaterRepository } from "@/pages/theater/hooks/use-theater-repository";

const NO_OPTIONAL_FIELDS: PlayOptionalFieldPresence = {
  premiereDate: false,
  ageGuidance: false,
};

/**
 * The theater twin of `MoviesEdit`. Until this page existed, a wrong title, image, cast or
 * genre on a Tina-created play was only fixable by a direct Firestore write (spec §3.8).
 *
 * `ShowGenresField` is reused verbatim: show genres are one collection, and the theater
 * vocabulary is the `inTheaters=true` slice of it (spec P2-e). The search returns whatever
 * the collection holds; seeding the theater labels is Phase 2's job, not the form's.
 */
export const PlaysEdit = () => {
  const { getPlay, updatePlay } = useTheaterRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<PlayEditFormType>();
  const [present, setPresent] =
    React.useState<PlayOptionalFieldPresence>(NO_OPTIONAL_FIELDS);
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const { list } = useNavigation();
  const setTitle = useDocumentTitle();

  const schemaProvider = useMemo(
    () => new ZodProvider(buildPlayEditSchema(present)),
    [present.premiereDate, present.ageGuidance],
  );

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    getPlay(id.toString())
      .then((response) => {
        // Fold each cast entry's stored `role` into `characterName` before the record
        // reaches the schema — see `normalizePlayRecord`'s doc comment for why the fold
        // cannot live on `playCastNestedSchema` itself.
        const normalized = normalizePlayRecord(response);
        const detected = playFieldPresence(normalized);
        // Lenient read, same policy as MoviesEdit: one unexpected enum value must not
        // blank the page.
        const data = parseRead(
          buildPlayEditSchema(detected),
          normalized,
          "play",
        ) as PlayEditFormType;
        setPresent(detected);
        setValues(data);
        setTitle("Edit " + data.title);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get play",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<PlayEditFormType, any, PlayEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: PlayEditFormType,
    form: UseFormReturn<PlayEditFormType, any, PlayEditFormType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    setWarnWhen(false);
    await updatePlay({ id: allValues.id, ...dirty });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: {
                showGenres: ShowGenresField,
                playCast: PlayCastField,
              },
            })}
          >
            <BottomActionBar className="justify-between">
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
              <DeleteButton
                resource="plays"
                id={id?.toString()}
                onSuccess={() => list("plays")}
              />
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
